import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'
import { Button, Row, Col, Tag, Form, message, Modal, Input, DatePicker, useCRUDController, Radio, Spin } from "../ui/ot"
import { SmartTaskSchedulingTaskTable } from "../pmis/smart_task_scheduling_task";
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { StaffForm } from '../organization_structure/personal_information_management';
import styled, { css } from "styled-components";

/* =====================
   基礎元件：Card / CardContent
===================== */
export const Card = styled.div`
  border-radius: 0.75rem;       /* rounded-xl 舒服一些 */
  border: 1px solid #e5e7eb;    /* gray-200 */
  background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
`;

export const CardContent = styled.div`
  padding: 1rem; /* p-4 */
`;

/* =====================
   Badge（支援 variant: solid | outline、tone: green | orange | gray）
===================== */
const toneMap = {

    orange: { fg: "#9a3412", bg: "#ffedd5", border: "#fed7aa" },  // text-orange-800 ...
    gray: { fg: "#374151", bg: "#f3f4f6", border: "#e5e7eb" },  // text-gray-700 ...
    blue: {
        text: "rgba(239, 246, 255, .5)", // blue-50/50
        fg: "#1d4ed8", // blue-600
        bg: "#dbeafe", // blue-100
        border: "#1d4ed8",
        dot: "#3b82f6", // blue-500
    },
    green: {
        text: "rgba(240, 253, 244, .5)",
        fg: "#15803d",
        bg: "#dcfce7",
        border: "#166534",
        dot: "#22c55e",
    },
    red: {
        text: "rgba(254, 242, 242, .5)",
        fg: "#b91c1c",
        bg: "#fee2e2",
        border: "#b91c1c",
        dot: "#ef4444",
    },
    purple: {
        text: "rgba(250, 245, 255, .5)", // purple-50/50
        fg: "#6b21a8", // purple-700
        bg: "#f3e8ff", // purple-100
        border: "#6b21a8",
        dot: "#a855f7", // purple-500
    }
};

export const Badge = styled.span.attrs(({ $variant }) => ({
    role: "status",
    "data-variant": $variant || "outline",
}))`
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  line-height: 1;
  border-radius: 9999px;
  font-size: 1rem;        /* text-base */
  padding: 0.25rem 0.75rem;

  ${({ $tone = "gray", $variant = "outline" }) => {
        const t = toneMap[$tone] || toneMap.gray;
        if ($variant === "solid") {
            return css`
        color: ${t.fg};
        background: ${t.bg};
        border: 1px solid ${t.bg};
      `;
        }
        return css`
      color: ${t.fg};
      background: transparent;
      border: 1px solid ${t.border};
    `;
    }}
`;

/* =====================
   Checkbox（自訂樣式，支援 checked、disabled）
===================== */
const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const StyledCheckbox = styled.span`
  display: inline-flex;
  width: 1.125rem;   /* 18px */
  height: 1.125rem;
  border-radius: 0.375rem;
  border: 2px solid #e5e7eb; /* gray-200 */
  background: #fff;
  transition: all .15s ease;

  ${HiddenCheckbox}:focus-visible + & {
    outline: 2px solid #60a5fa;  /* focus ring: blue-400 */
    outline-offset: 2px;
  }

  ${HiddenCheckbox}:checked + & {
    border-color: #fb923c;       /* orange-400 */
    background: #fb923c;
  }

  /* 打勾符號 */
  &::after {
    content: "";
    display: block;
    width: 0.5rem;
    height: 0.25rem;
    border: 2px solid #fff;
    border-top: 0;
    border-right: 0;
    transform: rotate(-45deg) scale(0);
    margin: auto;
    transition: transform .15s ease;
  }

  ${HiddenCheckbox}:checked + &::after {
    transform: rotate(-45deg) scale(1);
  }

  ${HiddenCheckbox}:disabled + & {
    opacity: .5;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  cursor: pointer;
`;

export function Checkbox({ checked, onChange, disabled, className, ...props }) {
    return (
        <CheckboxLabel className={className}>
            <HiddenCheckbox
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                {...props}
            />
            <StyledCheckbox />
        </CheckboxLabel>
    );
}

/* =====================
   你的 Reservation List 畫面（改用上面自製元件）
===================== */


const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QueueItem = styled.div`
  border: 1px solid #e5e7eb; /* gray-200 */
  border-radius: 0.5rem;      /* rounded-lg */
  padding: 1rem;
  background-color: #ffffff;
`;

const QueueItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const QueueItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StyledOrangeBorderCheckbox = styled(Checkbox)`
  /* 自訂外觀：邊框橘色 */
  ${StyledCheckbox} {
    border-color: #fb923c; /* orange-400 */
  }
`;

const QueueItemRight = styled.div`
  text-align: right;

  .id {
    font-weight: 700;
    font-size: 1.125rem; /* text-lg */
    color: #111827;      /* gray-900 */
  }

  .number {
    font-size: 1rem;
    color: #4b5563;      /* gray-600 */
  }
`;

const QueueItemContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  font-size: 1rem;
`;

const RowStyle = styled.div`
  display: flex;
  justify-content: space-between;

  .label {
    color: #4b5563; /* gray-600 */
  }

  .value {
    font-weight: 500;
  }
`;

const QueueManagement = ({
    actionRef: propsActionRef,
    self = false,
    propsForm,
    onFormFinish = () => { },
    defaultSelectedRowsInfo = [],
    defaultSelectedRowsKeys = [],
    keyPrefix = "queue_management.queue_management",
    hasValue = false,
    mode = 'admin',
    showMode,
    showSearch,
    reloadButton,
    manageColumnsButton,
    endMessage,
    propsRangeFilter = false,
    propsSubmit = false,
    propsExport = true,
    propsPost = true,
    extraParams = {},
    propsShowKeys = [
        { key: "task_id", name: "task_id", hidden: false, require: false, formOnly: true },
        { key: "task_name", name: "task_name", hidden: true, require: false },
        { key: "task_explaination", name: "task_explaination", hidden: true, require: false },
        { key: "person_in_charge", name: "person_in_charge", hidden: true, require: false },
        { key: "executor", name: "executor", hidden: true, require: false },
        { key: "start_time", name: "start_time", hidden: true, require: false },
        { key: "end_time", name: "end_time", hidden: true, require: false },
        { key: "progress", name: "progress", hidden: true, require: false },
        { key: "category_name", name: "category_name", hidden: true, require: false },
        { key: "priority_name", name: "priority_name", hidden: true, require: false },
        { key: "label_name", name: "label_name", hidden: true, require: false },
        { key: "status_name", name: "status_name", hidden: true, require: false },
        { key: "application_submitted_text", name: "application_submitted_text", hidden: true, require: false },
        { key: "create_user_name", name: "create_user_name", hidden: true, require: false },
        { key: "birthday", name: "birthday", hidden: false, require: false },
        { key: "lunar_birthday", name: "lunar_birthday", hidden: true, require: false },

        { key: "serial_name_check", name: "serial_name_check", hidden: true, require: false, },
        { key: "staff_serial_name", name: "staff_serial_name", hidden: false, require: false, },
        { key: "staff_english_name", name: "staff_english_name", hidden: true, require: false, },
        { key: "user_department_role_data", name: "user_department_role_data", hidden: true, require: false, },
        { key: "email", name: "email", hidden: true, require: false, },
        { key: "custom_line_id", name: "custom_line_id", hidden: true, require: false, },
        { key: "address", name: "address", hidden: false, require: false, },
        { key: "staff_phone", name: "staff_phone", hidden: false, require: false, },
        { key: "property_json", name: "property_json", hidden: true, require: false, },
    ],
    ...props
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation('translation', { keyPrefix })
    const [form_] = Form.useForm();
    const form = propsForm || form_

    const [staffId, setStaffId] = useState(null);
    const [ModalOpen, setModalOpen] = useState(
        // mode === "self" ? true : 
        false
    )
    const actionRef_ = useRef(null)
    const actionRef = propsActionRef ?? actionRef_

    const getFormValues = () => {
        let values = {}
        values = form.getFieldsValue()
        return values
    }
    const onFinish = () => {
        const values = getFormValues();
        onFormFinish({
            ...values,
        })
    }
    const onSelectChange = (selectedRowsInfo) => {
        form.setFieldsValue({ selectedRowsInfo: selectedRowsInfo })
    }

    const refStaffTimer = useRef(setTimeout(() => { }, 0));
    const staffController = useCRUDController({ apiRoute: '/organization_structure/api/staff' })
    const staffSelfController = useCRUDController({ apiRoute: '/organization_structure/api/staff_self' })
    const staffTaskAreaController = useCRUDController({
        apiRoute: `/queue_management/backend/staff_task_area` + (self ? "_self" : ""),
        onFinish: (oResponse) => {
            if (oResponse.status === 'error') {
                message.error(oResponse.values.errorInfo)
                return
            }
            if (oResponse.axiosMode === 'post') {
                message.success('預約成功')
                actionRef.current.reloadAndRest()
            }
        }, 
    })
    const [staffForm] = Form.useForm()
    const formOnValuesChange = (formValues, { name } = {}, propsForm) => {
        if (String(name).endsWith('staff_name')) {
            const index = name.lastIndexOf('.');
            const result = name.substring(0, index);
            clearTimeout(refStaffTimer.current);
            refStaffTimer.current = setTimeout(() => {
                staffController.getData({
                    staff_name_only: propsForm.getFieldValue(result + '.staff_name'),
                }).then(response => {
                    if (response?.data?.length > 0) {
                        Modal.confirm({
                            title: '既有人員',
                            content: (<>
                                <Form
                                    form={staffForm}
                                >
                                    <Form.Item
                                        name={'staff'}
                                    >
                                        <Radio.Group>
                                            {response?.data?.map((item, index) => <>
                                                <Radio value={index}>姓名：{item?.staff_name}，生日：{item?.birthday}</Radio>
                                            </>)}
                                        </Radio.Group>
                                    </Form.Item>
                                </Form>
                            </>),
                            onOk: () => {
                                const staff = staffForm.getFieldValue('staff')
                                console.log(staffForm.getFieldsValue())
                                if (staff !== undefined) {
                                    console.log({ result })
                                    console.log({ staff })
                                    console.log({ staff: response?.data?.[staff] })
                                    propsForm.setFieldValue(result, {
                                        ...response?.data?.[staff]
                                    })
                                }
                            },
                            okText: '帶入資訊',
                            cancelText: '關閉'
                        })
                    }
                })
            }, 1000)
        }
    }

    useEffect(() => {
        if (self) {
            staffSelfController.getData()
            staffTaskAreaController.getData({ start_time: dayjs().format('YYYY-MM-DD') })
        }
    }, [])
    const initialValues = {
        queue_task_data: [
            {
                queue_staff_data: [
                    {
                        staff_id: staffSelfController.ResponseData?.find(_ => true)?.staff_id
                    }
                ],
                queue_area_data: [
                    {
                        area_id: 4
                    }
                ],
            }
        ],
        task_name: "預約",
        start_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    }
    return (
        <Row>
            <Col span={24}>
                {(self && staffSelfController.ResponseData?.find(_ => true)?.staff_id === undefined) && <>
                    <Spin />資料讀取中 請稍候...
                </>}
                {(!self || staffSelfController.ResponseData?.find(_ => true)?.staff_id) && <>
                    <SmartTaskSchedulingTaskTable
                        actionRef={actionRef}
                        api={`/queue_management/backend/staff_task_area` + (self ? "_self" : "")}
                        keyPrefix={keyPrefix}
                        propsModalOpen={ModalOpen}
                        propsSetModalOpen={setModalOpen}
                        defaultSelectedRowsInfo={defaultSelectedRowsInfo}
                        defaultSelectedRowsKeys={defaultSelectedRowsKeys}
                        onSelectChange={onSelectChange}
                        customColumns={[
                            {
                                title: t('queue_task_data'),
                                sorter: false,
                                dataIndex: 'queue_task_data',
                                key: 'queue_task_data',
                                width: "8rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => {
                                    const queue_area_data = record?.queue_task_data?.find(_ => true).queue_area_data?.find(_ => true);
                                    const title = queue_area_data?.property_json?.title
                                    const background = queue_area_data?.property_json?.background
                                    const status = queue_area_data?.area_postion_data?.find(_ => true)?.name_language_data?.find(_ => true)?.language_culture_value;
                                    return (
                                        <Row justify='center'>
                                            <Col>
                                               <Tag style={{ color: title, background: background }} color={title ? '' : 'secondary' }>
                                                  {status ?? '已離館'}
                                               </Tag>
                                            </Col>
                                        </Row>
                                    );
                                }
                            },
                            {
                                title: t('task_id'),
                                sorter: true,
                                dataIndex: 'task_id',
                                key: 'task_id',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search"
                            },
                            {
                                title: t('start_time'),
                                sorter: true,
                                dataIndex: 'start_time',
                                key: 'start_time',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                            },
                            {
                                title: t('number'),
                                sorter: false,
                                dataIndex: 'row_number_partition_date_order_task_id',
                                key: 'row_number_partition_date_order_task_id',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search"
                            },
                            {
                                title: t('staff_name'),
                                sorter: false,
                                dataIndex: 'staff_name',
                                key: 'staff_name',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => <>
                                    <Row justify={'center'}>
                                        {record?.queue_task_data.find(_ => true)?.queue_staff_data?.find(_ => true)?.staff_name}
                                    </Row>
                                </>
                            },
                            {
                                title: t('birthday'),
                                sorter: false,
                                dataIndex: 'birthday',
                                key: 'birthday',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => <>
                                    <Row justify={'center'}>
                                        {record?.queue_task_data.find(_ => true)?.queue_staff_data?.find(_ => true)?.birthday}
                                    </Row>
                                </>
                            },
                            {
                                title: t('birthday_lunar'),
                                sorter: false,
                                dataIndex: 'lunar_birthday',
                                key: 'lunar_birthday',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => <>
                                    <Row justify={'center'}>
                                        {record?.queue_task_data.find(_ => true)?.queue_staff_data?.find(_ => true)?.lunar_birthday}
                                    </Row>
                                </>
                            },
                            {
                                title: t('staff_phone'),
                                sorter: false,
                                dataIndex: 'staff_phone',
                                key: 'staff_phone',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => <>
                                    <Row justify={'center'}>
                                        {record?.queue_task_data.find(_ => true)?.queue_staff_data?.find(_ => true)?.staff_phone}
                                    </Row>
                                </>
                            },
                            {
                                title: t('address'),
                                sorter: false,
                                dataIndex: 'address',
                                key: 'address',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search",
                                render: (text, record) => <>
                                    <Row justify={'center'}>
                                        {record?.queue_task_data.find(_ => true)?.queue_staff_data?.find(_ => true)?.address}
                                    </Row>
                                </>
                            },
                            {
                                title: t('task_name'),
                                sorter: false,
                                dataIndex: 'task_name',
                                key: 'task_name',
                                width: "10rem",
                                canFilter: false,
                                filterType: "search"
                            },
                        ]}
                        // extraActionRender={(item) => {
                        //     let transfer_data = {
                        //         ...item?.sop_data?.find(_ => true),
                        //         self: false
                        //     }
                        //     return (<>{(item?.application_submitted === 0) ? <></> :
                        //         <Row gutter={[8, 8]}>
                        //             <Col span={24}>
                        //                 <Button
                        //                     variant={"sop_display"}
                        //                     style={{
                        //                         color: '#3FA68C'
                        //                     }}
                        //                     onClick={() => {
                        //                         navigate('../sop_display', { state: transfer_data || {} });
                        //                     }}
                        //                 >
                        //                     {t('sop_display')}
                        //                 </Button>
                        //             </Col>
                        //         </Row>}
                        //     </>)
                        // }}
                        toolBarRender={(item) => {
                            return ([
                                ...item,
                                ...(self ? [
                                    <Button onClick={() => {
                                        /* 3、6不會進 */
                                        let is_re_post = 1;
                                        staffTaskAreaController.ResponseData?.forEach(item => {
                                            if (item?.queue_task_data?.find(_ => true)?.queue_area_data?.find(_ => true)?.is_re_post === 0) {
                                                is_re_post = 0;
                                            }
                                        })
                                        if(is_re_post===1){
                                            Modal.confirm({
                                                title: '我要預約',
                                                content: '確定要預約今天嗎？',
                                                onOk: () => {
                                                    staffTaskAreaController.postData( [initialValues] )
                                                },
                                                okText: '送出',
                                                cancelText: '取消',
                                            })
                                        }else{
                                            Modal.info({
                                                title: '訊息',
                                                content: '您已在預約名單中',
                                                okText: '關閉',
                                                cancelText: '取消',
                                            })
                                        }
                                    }
                                    }>我要預約</Button>
                                ] : [])
                            ])
                        }}

                        initialValues={initialValues}
                        customFormItems={(fieldName, formValues, onValuesChange = () => { }, propsCustomForm, field, propsOnFinish) => {
                            return (<>
                                <Form
                                    form={propsCustomForm}
                                    onFinish={propsOnFinish}
                                >
                                    <Form.Item
                                        label={t('start_time')}
                                        name={[field?.name, 'start_time']}
                                    >
                                        <DatePicker.Time />
                                    </Form.Item>
                                    <Form.List
                                        name={[field?.name, 'queue_task_data']}
                                    >
                                        {fields => <>
                                            {fields?.map(subField => <>
                                                <Form.Item
                                                    hidden
                                                    name={[subField?.name, 'queue_area_data']}
                                                >
                                                </Form.Item>
                                                <Form.Item
                                                    label={t('number')}
                                                    name={[subField?.name, 'number']}
                                                >
                                                    <label>自動產生</label>
                                                </Form.Item>
                                                <Row>
                                                    <Col span={24}
                                                    >
                                                        <Form.List
                                                            name={[subField?.name, 'queue_staff_data']}
                                                        >
                                                            {subFields => <>
                                                                {subFields?.map(subSubField => <>
                                                                    <StaffForm
                                                                        propsForm={propsCustomForm}
                                                                        onFormFinish={propsOnFinish}
                                                                        field={subSubField}
                                                                        propsShowKeys={propsShowKeys}
                                                                        genderOption="radio"
                                                                        formMode
                                                                        formOnValuesChange={formOnValuesChange}
                                                                        staff_id={staffSelfController.ResponseData?.find(_ => true)?.staff_id}
                                                                        mode={self ? "information" : undefined}
                                                                    />
                                                                </>)}
                                                            </>}
                                                        </Form.List>
                                                    </Col>
                                                </Row>
                                            </>)}
                                        </>}
                                    </Form.List>
                                    <Form.Item
                                        label={t('task_name')}
                                        name={[field?.name, 'task_name']}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Form>
                            </>)
                        }}
                        propsRowKey='task_id'
                        showDefaultColumn={true}
                        propsShowKeys={propsShowKeys}
                        // propsDefaultAxiosMode={mode === "self" ? "post" : undefined}
                        filterMode="create_time"
                        propsWidth={700}
                        formMode
                        propsSubmit={propsSubmit}
                        formOnValuesChange={formOnValuesChange}
                        // propsPost={self ? staffSelfController.ResponseData?.find(_ => true)?.staff_id !== undefined : true}
                        propsPost={propsPost}
                        showMode={showMode}
                        showSearch={showSearch}
                        reloadButton={reloadButton}
                        manageColumnsButton={manageColumnsButton}
                        endMessage={endMessage}
                        propsRangeFilter={propsRangeFilter}
                        propsExport={propsExport}
                        propsRowSelection={{
                            type: (self ? '' : 'checkbox')
                        }}
                        extraParams={extraParams}
                        propsInitialValues={initialValues}
                        propsFilterCustomColumns={false}
                        cardRender={(record) => {
                            const COLOR_MAP = new Map([
                                ['逾時未進入者', 'red'],
                                ['已入館讀者', 'purple'],
                                ['待入館讀者', 'blue'],
                                ['', 'green'],
                            ]);

                            const getToneColor = (label, fallback = 'purple') =>
                                label == null ? fallback : (COLOR_MAP.get(String(label).trim()) ?? fallback);

                            const label =
                                record?.queue_task_data?.[0]
                                    ?.queue_area_data?.[0]
                                    ?.area_postion_data?.[0]
                                    ?.name_language_data?.[0]
                                    ?.language_culture_value;

                            const toneColor = getToneColor(label); // 期望得到 'red' / 'purple' / 'blue'
                            console.log(toneColor)
                            return <>
                                <Card>
                                    <QueueList>
                                        <QueueItem key={record.task_id}>
                                            <QueueItemHeader>
                                                <QueueItemLeft>
                                                    <Badge
                                                        $variant="outline"
                                                        $tone={toneColor}
                                                    >
                                                        {record?.queue_task_data?.find(_ => true)?.queue_area_data?.find(_ => true)?.area_postion_data?.find(_ => true)?.name_language_data?.find(_ => true)?.language_culture_value || "-"}                                                                </Badge>
                                                </QueueItemLeft>

                                                <QueueItemRight>
                                                    <div className="id">#{record?.task_id}</div>
                                                    <div className="number">號碼: {record?.row_number_partition_date_order_task_id}</div>
                                                </QueueItemRight>
                                            </QueueItemHeader>

                                            <QueueItemContent>
                                                <RowStyle>
                                                    <span className="label">預約日期:</span>
                                                    <span className="value">{record?.start_time}</span>
                                                </RowStyle>
                                            </QueueItemContent>
                                        </QueueItem>

                                    </QueueList>
                                </Card>
                            </>
                        }}
                        cardModeSetting={{
                            columnsCount: 1,
                            singleRowHeight: 60,
                        }}
                    />
                </>}
            </Col>
        </Row>
    )
}

export default QueueManagement;